/**
 * A designer's REUSABLE SECTION library (D97 / RS-3), and the three things it
 * has to be honest about.
 *
 * A reusable section is a configured section saved by name so it can be dropped
 * onto another wall later. This module owns the LOCAL half: one IndexedDB meta
 * key, `reusable-section-components`, read and written as a whole.
 *
 * The library FOLLOWS THE DESIGNER across devices. The local key is the offline
 * copy, not the extent of it: `sketch.reusable_sections` (applied 2026-08-27)
 * holds one owner-scoped row per component, and the sync engine's third entity
 * (#112) pushes what this device saved and merges back what the designer saved
 * elsewhere. Save on a laptop, open a tablet, and it is there once that device
 * syncs.
 *
 * WHY THIS IS A MODULE RATHER THAN THREE LINES IN `app.js`.
 *
 * It was three lines in `app.js`, and all three were wrong in ways that could
 * not be tested from anywhere:
 *
 *   1. A failed read and an empty library were the same screen, with no way for
 *      a designer to tell which. The code read `getMeta(key).then(…)` with a
 *      `.catch(() => {})` on the end — but **that catch never fired**, because
 *      `getMeta` does not reject. Every failure inside it (no storage at all,
 *      an IndexedDB open blocked by a stale tab, a transaction that throws, a
 *      request that errors) resolves to `null` — the same value a key that was
 *      never written returns. The swallow was dead code sitting on top of a
 *      conflation one layer down, which is why `getMetaResult` exists now.
 *
 *   2. Worse, and the reason this is a correctness fix rather than a wording
 *      one: SAVING after a failed read DESTROYED the library. The list was
 *      `[]`, the new component was pushed onto it, and the whole key was
 *      written back — so a designer with ten saved sections whose read failed
 *      once, and who then saved an eleventh, was left with exactly one.
 *      Silent, permanent, on the device that still had the data a moment
 *      earlier.
 *
 *   3. Nothing anywhere said how far the library reached, so an empty list on a
 *      second device looked like loss rather than like a limit.
 *
 * (1) and (2) are fixed here. (3) is answered by `libraryNote()`, which the
 * panel renders — and it is now a statement of fact rather than of a limit,
 * because the cross-device half shipped (ADR 0100 and its amendment).
 *
 * HISTORY, because this comment misled a reader and cost a re-brief. Until
 * 2026-08-30 the text above still said the cross-device work was "gated on the
 * owner authorising a migration and deliberately NOT in this module". Both had
 * been false since 2026-08-27: the migration was applied and #112 wired the
 * entity. The stale claim outlived the truth in the most-read file on the
 * subject, and this module's own user-facing copy — `libraryNote()`, which
 * already said sections "follow you to any device" — was the only thing in here
 * contradicting it. When a doc comment and the string a designer reads
 * disagree, the string is the one being tested.
 *
 * WHAT THIS MODULE STILL OWNS, so the boundary is not guessed at again: the
 * local key, the load-state machine, and the refusal to write over a library it
 * could not read. The engine owns push, pull and cursors; the table owns
 * isolation. `applyRemote` is the seam between them, and it refuses unless the
 * state is 'ready' for exactly the reason (2) exists.
 *
 * The state machine is the point: 'loading' → 'ready' | 'error', and 'error' is
 * never silently the same as an empty 'ready'.
 */

/** Where the library is written LOCALLY. One key, deliberately — see ADR 0100.
 *  The server copy is one row per component in `sketch.reusable_sections`. */
export const LIBRARY_META_KEY = 'reusable-section-components';

/**
 * What the panel says about scope, per state.
 *
 * Exported so the disclosure is one string rather than one per caller, and so a
 * test can assert the ERROR wording never reads like emptiness. `count` is only
 * consulted when the library loaded.
 */
export function libraryNote({ state, count = 0 } = {}) {
  if (state === 'loading') return 'Loading your saved sections…';
  if (state === 'error') {
    return 'Your saved sections could not be loaded on this device. '
      + 'They are not lost — saving a new one is disabled until they load, so nothing overwrites them. Reload to try again.';
  }
  if (!count) return 'Save a configured section to reuse it. Saved sections sync to your account when you sign in.';
  return 'Synced to your account when you sign in — saved sections follow you to any device.';
}

/**
 * The library, over an injected meta store.
 *
 * `getMetaResult`/`setMeta` are injected rather than imported so the failure
 * paths are reachable from a test: a store that cannot be read is the whole
 * point of this module, and a real IndexedDB will not fail on demand.
 *
 * @param {{getMetaResult: Function, setMeta: Function, makeId: Function}} deps
 */
export function createReusableSectionLibrary({ getMetaResult, setMeta, makeId }) {
  let components = [];
  let state = 'loading';
  let error = null;

  /**
   * Reads the library once. Never throws: it records the failure instead, so
   * the caller renders an honest panel rather than an empty one.
   *
   * IT READS THROUGH `getMetaResult`, NOT `getMeta`, AND THAT IS THE FIX.
   * `getMeta` resolves to `null` on every failure — no storage, an IndexedDB
   * open blocked by a stale tab, a transaction that throws — which is the same
   * value a key that was never written returns. Reading it would make a failed
   * read indistinguishable from a new designer's empty library, and the next
   * save would then write that empty library back over the real one. The
   * original code's swallowed `.catch()` never even fired: there was no error
   * to catch, only a null.
   *
   * A stored value that is not an array IS an empty library, not an error —
   * that is a key that has never been written, which is what a new designer
   * has. Only a reported read FAILURE is an error.
   */
  async function load() {
    try {
      const result = await getMetaResult(LIBRARY_META_KEY);
      if (!result?.ok) {
        // The list stays EMPTY and `state` is what stops it being read as "no
        // library": `add` refuses while this holds, so an empty list here can
        // never be written back over a full one.
        components = [];
        state = 'error';
        error = result?.reason ?? 'The library could not be read.';
        return status();
      }
      components = Array.isArray(result.value) ? result.value.filter(isComponent) : [];
      state = 'ready';
      error = null;
    } catch (cause) {
      // `getMetaResult` is contracted not to throw; this is belt-and-braces so
      // an injected store that does cannot turn into a silently empty library.
      components = [];
      state = 'error';
      error = cause instanceof Error ? cause.message : String(cause ?? 'unknown');
    }
    return status();
  }

  /** A stored entry that is actually usable — a name and a section body. */
  function isComponent(entry) {
    return !!entry && typeof entry === 'object' && typeof entry.name === 'string' && !!entry.section;
  }

  /** `{state, count, error, note}` — everything the panel needs to be honest. */
  function status() {
    return { state, count: components.length, error, note: libraryNote({ state, count: components.length }) };
  }

  /** The saved components, copied so a caller cannot mutate the library. */
  function list() {
    return components.map((component) => ({ ...component }));
  }

  /**
   * Saves a configured section under a name.
   *
   * REFUSES while the library has not successfully loaded. That refusal is the
   * data-loss fix: writing here replaces the whole key, so saving on top of a
   * library we failed to read would overwrite every entry in it with this one.
   * A designer is told to reload rather than silently losing the other ten.
   *
   * @returns {{ok: true, component: object} | {ok: false, reason: string}}
   */
  async function add(section, rawName) {
    if (state !== 'ready') {
      return {
        ok: false,
        reason: state === 'loading'
          ? 'Your saved sections are still loading — try again in a moment.'
          : 'Your saved sections could not be loaded, so saving is disabled to avoid overwriting them. Reload and try again.',
      };
    }
    const name = String(rawName ?? '').trim().slice(0, 80);
    if (!name) return { ok: false, reason: 'Give the section a name.' };
    if (!section || typeof section !== 'object') return { ok: false, reason: 'There is no section to save.' };

    // The copy is detached from the wall it came from: an id, a host and a
    // stack parent are all facts about WHERE it was, not about what it is.
    const copy = structuredClone(section);
    delete copy.id; delete copy.wallId; delete copy.stackOnId; delete copy.fromBucket;
    const component = { id: makeId(), name, section: copy };

    const next = [...components, component];
    try {
      await setMeta(LIBRARY_META_KEY, next);
    } catch (cause) {
      // Do NOT keep it in memory: the panel would then show a section that is
      // not saved, and the next successful save would write it as though it
      // had been. Report instead.
      return { ok: false, reason: `Could not save: ${cause instanceof Error ? cause.message : String(cause)}` };
    }
    components = next;
    return { ok: true, component };
  }

  /** One component by id (copied), or null. The sync push reads through this. */
  function get(id) {
    const component = components.find((c) => c.id === id);
    return component ? { ...component } : null;
  }

  /**
   * Merges PULLED components into the library — the sync engine's write path
   * (D97; ADR 0100 amendment). Additive by id: an incoming live component
   * replaces the same id or is appended; an incoming TOMBSTONE (`deletedAt`)
   * removes the id; components the pull does not mention — including local
   * saves not yet pushed — are untouched. Never a wholesale replace: that is
   * the overwrite this module exists to prevent, one caller further out.
   *
   * REFUSES unless the library loaded, for the same reason `add` does: this
   * write replaces the whole key, and merging into a library we could not
   * read would overwrite every component in it with only the pulled set.
   *
   * @returns {{ok: true, changed: number} | {ok: false, reason: string}}
   */
  async function applyRemote(remote) {
    if (state !== 'ready') {
      return { ok: false, reason: 'The library has not loaded on this device, so pulled sections were not applied (nothing was overwritten).' };
    }
    const byId = new Map(components.map((c) => [c.id, c]));
    let changed = 0;
    for (const incoming of Array.isArray(remote) ? remote : []) {
      if (!incoming?.id) continue;
      if (incoming.deletedAt) {
        if (byId.delete(incoming.id)) changed += 1;
        continue;
      }
      if (!isComponent(incoming)) continue;
      const prev = byId.get(incoming.id);
      if (prev && prev.updatedAt === incoming.updatedAt) continue;
      byId.set(incoming.id, {
        id: incoming.id, name: incoming.name, section: incoming.section,
        ...(incoming.updatedAt ? { updatedAt: incoming.updatedAt } : {}),
      });
      changed += 1;
    }
    if (!changed) return { ok: true, changed: 0 };
    const next = [...byId.values()];
    try {
      await setMeta(LIBRARY_META_KEY, next);
    } catch (cause) {
      return { ok: false, reason: `Could not apply pulled sections: ${cause instanceof Error ? cause.message : String(cause)}` };
    }
    components = next;
    return { ok: true, changed };
  }

  return { load, add, list, get, applyRemote, status };
}
